import styled from 'styled-components';

import { colors, radius } from '../../../../styles/tokens';

export const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${colors.surface};
`;

export const KioskHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 40px;
  border-bottom: 1px solid ${colors.outlineVariant}40;

  @media (max-width: 1199px) {
    padding: 16px 20px;
  }
`;

export const HeaderGymName = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: ${colors.onSurface};
`;

export const HeaderBrand = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${colors.onSurfaceVariant};
`;

export const Container = styled.div`
  padding: 40px;
  min-height: 100%;
  max-width: 720px;
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.01em;
  margin-bottom: 8px;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.onSurfaceVariant};
`;

export const SearchWrapper = styled.div`
  position: relative;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 18px 20px;
  border-radius: ${radius.md};
  border: 1px solid ${colors.outlineVariant};
  background-color: ${colors.surfaceContainerLow};
  color: ${colors.onSurface};
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }
`;

export const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ResultRow = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: ${radius.md};
  border: 1px solid ${colors.outlineVariant}40;
  background-color: ${colors.surfaceContainerLow};
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${colors.primary};
  }
`;

export const ResultAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: ${radius.DEFAULT};
  object-fit: cover;
`;

export const ResultName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.onSurface};
  display: block;
`;

export const ResultUsername = styled.span`
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
`;

export const SelectedStudentCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};
`;

export const SelectedStudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  span {
    font-size: 18px;
    font-weight: 800;
    color: ${colors.onSurface};
  }
`;

export const ChangeStudentButton = styled.button`
  padding: 10px 18px;
  border-radius: ${radius.full};
  border: 1px solid ${colors.outlineVariant};
  background: transparent;
  color: ${colors.onSurfaceVariant};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
    color: ${colors.onSurface};
  }
`;

export const SplitPicker = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SplitButton = styled.button<{ active: boolean }>`
  padding: 16px 24px;
  border-radius: ${radius.md};
  border: 2px solid
    ${props => (props.active ? colors.primary : colors.outlineVariant)};
  background-color: ${props =>
    props.active ? `${colors.primary}1a` : colors.surfaceContainerLow};
  color: ${props => (props.active ? colors.primary : colors.onSurface)};
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
`;

export const PreviewCard = styled.div`
  padding: 24px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};
`;

export const PreviewTitle = styled.h3`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 16px;
`;

export const ExerciseRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.outlineVariant}30;

  &:last-child {
    border-bottom: none;
  }

  span:first-child {
    font-size: 13.5px;
    color: ${colors.onSurface};
  }

  span:last-child {
    font-size: 12.5px;
    color: ${colors.onSurfaceVariant};
  }
`;

export const PrintButton = styled.button`
  width: 100%;
  padding: 20px;
  border-radius: ${radius.full};
  border: none;
  background-color: ${colors.primary};
  color: ${colors.onPrimary};
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  font-size: 13px;
  color: ${colors.onSurfaceVariant};
`;

export const StatusBanner = styled.div<{ tone: 'success' | 'error' }>`
  padding: 16px 20px;
  border-radius: ${radius.md};
  font-size: 13.5px;
  font-weight: 600;
  background-color: ${props =>
    props.tone === 'success' ? '#32a54a1a' : `${colors.primary}1a`};
  color: ${props => (props.tone === 'success' ? '#32a54a' : colors.primary)};
`;
